// lib/api.js
import { mockSignin, mockMe, mockLogout } from "./mockAuth";
import { handleApiError } from "../plugins/errorHandler";
import { clearSession } from "./session";

const MOCK =
  String(process.env.VUE_APP_MOCK_AUTH || "").toLowerCase() === "1" ||
  String(process.env.VUE_APP_MOCK_AUTH || "").toLowerCase() === "true";

// ✅ 런타임/빌드타임에서 한 곳만 보고 결정
const API_BASE =
  window.__APP_CONFIG__?.API_BASE ||
  process.env.VUE_APP_API_BASE || // Vue CLI
  ""; // 같은 오리진이면 빈 문자열

// 실제 fetch (쿠키 자동 동봉)
export async function api(path, opts = {}) {
  if (MOCK) return mockApi(path, opts); // ← 개발 모드면 모의 라우터로
  const url = API_BASE + path; // ✅ '/api' 프리픽스 제거
  try {
    const res = await fetch(url, {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(opts.headers || {}) },
      ...opts,
    });

    // 응답 상태 확인 및 에러 처리
    if (!res.ok) {
      const error = new Error(`HTTP ${res.status}`);
      error.response = res;

      // 마지막 수강권이 끝나 막힌 회원(401 AUTH_009): 서버가 쿠키를 지웠으니 저장한 회원 정보도 지운다.
      // 지우지 않으면 라우터가 계속 로그인된 것으로 보고 로그인 화면을 대시보드로 돌려보낸다(새로 고침 전까지)
      if (res.status === 401) {
        const body = await res
          .clone()
          .json()
          .catch(() => null);
        if (body?.error?.code === "AUTH_009") clearSession();
      }

      // 401 UNAUTHORIZED 에러는 팝업으로 표시하지 않음
      if (path !== "/v1/me") {
        handleApiError(error);
      }

      throw error;
    }

    return res;
  } catch (error) {
    // 네트워크 오류 등 fetch 자체에서 발생하는 에러
    if (!error.response) {
      handleApiError(error);
    }
    throw error;
  }
}

export async function apiJson(path, opts = {}) {
  if (MOCK) return mockApiJson(path, opts);

  try {
    const res = await api(path, opts);
    if (res.status === 401) throw new Error("UNAUTHORIZED");
    if (!res.ok) {
      const error = new Error(`HTTP ${res.status}`);
      error.response = res;
      throw error;
    }
    return res.json();
  } catch (error) {
    // UNAUTHORIZED 에러는 팝업으로 표시하지 않음 (로그인 페이지로 리다이렉트 등)
    handleApiError(error);

    throw error;
  }
}

/* ---------------- Mock Router ---------------- */
async function mockApi(path, opts = {}) {
  const p = path; // 프리픽스 제거했으니 그대로 사용

  try {
    if (p === "/v1/auth/sessions" && opts.method === "POST") {
      const body = safeJson(opts.body);
      const data = mockSignin(body);
      return fakeResponse(200, data);
    }
    if (p === "/v1/me") {
      const data = mockMe();
      return fakeResponse(200, data);
    }
    if (p === "/v1/auth/sessions" && opts.method === "DELETE") {
      // ✅ 실제 백엔드와 엔드포인트 통일
      const data = mockLogout();
      return fakeResponse(200, data);
    }
    return fakeResponse(404, { message: "Not Found (mock)" });
  } catch (e) {
    const status = e.status || 500;
    return fakeResponse(status, { message: e.message || "Mock error" });
  }
}

async function mockApiJson(path, opts = {}) {
  const res = await mockApi(path, opts);
  if (res.status === 401) throw new Error("UNAUTHORIZED");
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

function safeJson(text) {
  try {
    return text ? JSON.parse(text) : {};
  } catch {
    return {};
  }
}
function fakeResponse(status = 200, data = {}) {
  return {
    ok: status >= 200 && status < 300,
    status,
    async json() {
      return data;
    },
    async text() {
      return JSON.stringify(data);
    },
  };
}
